import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import axios, { AxiosError, type AxiosRequestConfig } from "axios";
import { HTTP_UNAUTHORIZED, PUBLIC_AUTH_PATHS } from "./publicAuthConstants";
import { unAuthorizedLogout } from "./unauthorizedLogout";
import { frontendLogger } from "./frontendLogger";

const getToken = (): { value?: string } | null => {
  try {
    const raw = localStorage.getItem("token");
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as { value?: string };
  } catch {
    return null;
  }
};

const isPublicAuthUrl = (url: string): boolean => {
  const path = url.split("?")[0];
  return (PUBLIC_AUTH_PATHS as readonly string[]).includes(path);
};

export const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = {
      baseUrl: "",
    },
  ): BaseQueryFn<
    {
      url: string;
      method: AxiosRequestConfig["method"];
      data?: AxiosRequestConfig["data"];
      params?: AxiosRequestConfig["params"];
      headers?: AxiosRequestConfig["headers"];
      responseType?: AxiosRequestConfig["responseType"];
      timeout?: AxiosRequestConfig["timeout"];
      onUploadProgress?: AxiosRequestConfig["onUploadProgress"];
    },
    any,
    unknown
  > =>
  async (
    {
      url,
      method,
      data,
      params,
      headers,
      responseType,
      timeout,
      onUploadProgress,
    },
    api,
  ) => {
    if (url.includes("/Incomlend/")) {
      return { error: { status: "403", data: "NOT_ALLOWED" } };
    }
    const accessToken = getToken()?.value;
    const publicAuthRequest = isPublicAuthUrl(url);
    const startedAt = performance.now();
    try {
      const requestUrl = /^https?:\/\//i.test(url) ? url : baseUrl + url;
      const result = await axios({
        url: requestUrl,
        method,
        data,
        params,
        responseType,
        timeout,
        onUploadProgress,
        signal: api.signal,
        headers: {
          ...(data instanceof FormData
            ? {}
            : { "Content-Type": "application/json" }),
          ...(accessToken && !publicAuthRequest
            ? { Authorization: `Bearer ${accessToken}` }
            : {}),
          ...headers,
        },
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError<{
        error?: string;
        message?: string;
        detail?: string;
      }>;
      // Public auth endpoints (including OTP confirmation) are unauthenticated.
      const unauthorizedHandled =
        err.response?.status === HTTP_UNAUTHORIZED && !publicAuthRequest;
      if (unauthorizedHandled) {
        unAuthorizedLogout(accessToken ?? null);
      }
      const requestWasCancelled =
        axios.isCancel(axiosError) || api.signal.aborted;
      if (
        !requestWasCancelled &&
        !unauthorizedHandled &&
        (!err.response || err.response.status >= 500)
      ) {
        frontendLogger.apiFailure({
          http_method: String(method ?? "GET").toUpperCase(),
          http_status: err.response?.status,
          route: url,
          duration_ms: Math.round(performance.now() - startedAt),
        });
      }
      const responseData = err.response?.data;
      return {
        meta: { unauthorizedHandled },
        error: {
          status: err.response?.status,
          data: responseData,
          //eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          message: responseData?.message || responseData?.detail || err.message,
        },
      };
    }
  };
